import React, { useState } from 'react';
import { ArrowLeftIcon, AwardIcon, SparklesIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BadgeGrid } from '../components/BadgeGrid';
import { useDriver } from '../context/DriverContext';
import { Badge } from '../types/tombola';
const GAMIFICATION_BADGES = [{
  icon: '⭐',
  name: 'Étoile'
}, {
  icon: '🔥',
  name: 'Feu'
}, {
  icon: '🚀',
  name: 'Fusée'
}, {
  icon: '💪',
  name: 'Biceps'
}, {
  icon: '🏆',
  name: 'Trophée'
}, {
  icon: '🎖️',
  name: 'Médaille militaire'
}, {
  icon: '🎁',
  name: 'Cadeau'
}, {
  icon: '🌟',
  name: 'Étoile brillante'
}, {
  icon: '🎉',
  name: 'Fête'
}, {
  icon: '💎',
  name: 'Diamant'
}, {
  icon: '🏅',
  name: 'Médaille'
}, {
  icon: '⚡',
  name: 'Éclair'
}, {
  icon: '🥇',
  name: 'Médaille or'
}, {
  icon: '🥈',
  name: 'Médaille argent'
}, {
  icon: '🥉',
  name: 'Médaille bronze'
}, {
  icon: '🎯',
  name: 'Cible'
}, {
  icon: '🎮',
  name: 'Manette'
}, {
  icon: '🎲',
  name: 'Dé'
}, {
  icon: '🎊',
  name: 'Confettis'
}, {
  icon: '🏵️',
  name: 'Rosette'
}];
export function BadgesPage() {
  const navigate = useNavigate();
  const {
    driverData
  } = useDriver();
  const [filter, setFilter] = useState<'all' | 'unlocked' | 'locked'>('all');
  if (!driverData) {
    navigate('/login');
    return null;
  }
  // Create a map of unlocked badge names for quick lookup
  const unlockedBadgeNames = new Set(driverData.badges.map(b => b.name.toLowerCase()));
  // Map gamification badges to Badge type with unlock status
  const allBadges: Badge[] = GAMIFICATION_BADGES.map((badge, index) => {
    const isUnlocked = unlockedBadgeNames.has(badge.name.toLowerCase());
    const unlockedBadge = driverData.badges.find(b => b.name.toLowerCase() === badge.name.toLowerCase());
    return {
      id: `badge-${index}`,
      name: badge.name,
      description: `Badge ${badge.name}`,
      icon: badge.icon,
      unlocked: isUnlocked,
      unlockedAt: unlockedBadge?.unlockedAt
    };
  });
  const unlockedBadges = allBadges.filter(b => b.unlocked);
  const lockedBadges = allBadges.filter(b => !b.unlocked);
  const mostRecentBadge = [...unlockedBadges].sort((a, b) => (b.unlockedAt?.getTime() || 0) - (a.unlockedAt?.getTime() || 0))[0];
  const recentlyUnlocked = [...unlockedBadges].sort((a, b) => (b.unlockedAt?.getTime() || 0) - (a.unlockedAt?.getTime() || 0)).slice(0, 3);
  const filteredBadges = filter === 'unlocked' ? unlockedBadges : filter === 'locked' ? lockedBadges : allBadges;
  return <div className="min-h-screen bg-gray-50 pb-20" data-id="element-148">
      <div className="max-w-lg mx-auto" data-id="element-149">
        <header className="bg-gradient-to-br from-amber-500 to-orange-500 px-4 pt-6 pb-8 relative overflow-hidden" data-id="element-150">
          <div className="absolute inset-0 pointer-events-none" data-id="element-151">
            {unlockedBadges.slice(0, 6).map((badge, index) => <div key={badge.id} className="absolute text-4xl opacity-10 blur-sm" style={{
            left: `${15 + index * 15}%`,
            top: `${20 + index % 3 * 25}%`,
            animation: `float ${3 + index * 0.5}s ease-in-out infinite`,
            animationDelay: `${index * 0.3}s`,
            transform: `rotate(${index * 15 - 30}deg)`
          }} data-id="element-152">
                {badge.icon}
              </div>)}
          </div>
          <button onClick={() => navigate('/')} className="text-white mb-4 flex items-center gap-2 hover:opacity-80 transition-opacity relative z-10" data-id="element-153">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-154" />
            <span className="font-medium" data-id="element-155">Retour</span>
          </button>
          <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-156">
            <div className="w-12 h-12 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-157">
              <AwardIcon className="w-7 h-7 text-white" data-id="element-158" />
            </div>
            <div data-id="element-159">
              <h1 className="text-2xl font-bold text-white" data-id="element-160">Badges</h1>
              <p className="text-white text-opacity-90 text-sm" data-id="element-161">
                Collection de récompenses
              </p>
            </div>
          </div>
          {mostRecentBadge && <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-4 mb-4 relative z-10 border border-white border-opacity-30" data-id="element-162">
              <div className="flex items-center gap-2 mb-2" data-id="element-163">
                <SparklesIcon className="w-4 h-4 text-yellow-200" data-id="element-164" />
                <span className="text-white text-opacity-90 text-xs font-medium" data-id="element-165">
                  Dernière récompense
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-166">
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center text-3xl shadow-lg" data-id="element-167">
                  {mostRecentBadge.icon}
                </div>
                <div className="flex-1" data-id="element-168">
                  <h3 className="text-white font-bold" data-id="element-169">
                    {mostRecentBadge.name}
                  </h3>
                  <p className="text-white text-opacity-80 text-sm" data-id="element-170">
                    {mostRecentBadge.unlockedAt?.toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
                  </p>
                </div>
              </div>
            </div>}
          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-4 relative z-10" data-id="element-171">
            <div className="flex items-center justify-between mb-2" data-id="element-172">
              <span className="text-white text-opacity-90 text-sm" data-id="element-173">
                Progression
              </span>
              <span className="text-white font-bold text-lg" data-id="element-174">
                {unlockedBadges.length}/{allBadges.length}
              </span>
            </div>
            <div className="w-full h-2 bg-white bg-opacity-20 rounded-full overflow-hidden" data-id="element-175">
              <div className="h-full bg-white rounded-full transition-all duration-500" style={{
              width: `${unlockedBadges.length / allBadges.length * 100}%`
            }} data-id="element-176" />
            </div>
          </div>
        </header>
        <div className="px-4 py-6 space-y-6" data-id="element-177">
          {recentlyUnlocked.length > 0 && <section data-id="element-178">
              <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-179">
                Récemment débloqués
              </h2>
              <div className="grid grid-cols-3 gap-3" data-id="element-180">
                {recentlyUnlocked.map(badge => <div key={badge.id} className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-4 shadow-sm border border-amber-200" data-id="element-181">
                    <div className="text-4xl mb-2 text-center" data-id="element-182">
                      {badge.icon}
                    </div>
                    <p className="text-xs text-gray-700 text-center font-medium line-clamp-2" data-id="element-183">
                      {badge.name}
                    </p>
                    <p className="text-xs text-gray-500 text-center mt-1" data-id="element-184">
                      {badge.unlockedAt?.toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short'
                })}
                    </p>
                  </div>)}
              </div>
            </section>}
          <section data-id="element-185">
            <div className="flex items-center justify-between mb-4" data-id="element-186">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-187">
                Tous les badges
              </h2>
              <div className="flex gap-2" data-id="element-188">
                <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === 'all' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-189">
                  Tous
                </button>
                <button onClick={() => setFilter('unlocked')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === 'unlocked' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-190">
                  Débloqués
                </button>
                <button onClick={() => setFilter('locked')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === 'locked' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-191">
                  Verrouillés
                </button>
              </div>
            </div>
            <BadgeGrid badges={filteredBadges} data-id="element-192" />
          </section>
        </div>
      </div>
      <style data-id="element-193">{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(var(--rotate-start, 0deg));
          }
          50% {
            transform: translateY(-20px) rotate(var(--rotate-end, 15deg));
          }
        }
      `}</style>
    </div>;
}